import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { ReactComponent as QQIcon } from '../resources/QQ-Icon.svg';
import Footer from '../components/Footer';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/welcome.css';
import allPOIs from '../resources/pois.json';

const TRENDING_AMENITY_ID = 1;
const TRENDING_LIMIT = 6;

function fetchTrendingPOIs() {
  return allPOIs
    .filter((p) => Array.isArray(p.amenities) && p.amenities.includes(TRENDING_AMENITY_ID))
    .slice(0, TRENDING_LIMIT);
}

const DEFAULT_WELCOME_HERO_BG =
  'https://storage.googleapis.com/quisqueyaquest-resources/poi/aguablanca8.jpg';

const HERO_BG_CROSSFADE_MS = 700;

/** Resolve when the bitmap is ready so opacity fades pixels instead of an empty layer. */
function loadHeroBackgroundImage(url) {
  return new Promise((resolve) => {
    const im = new Image();
    const finish = () => resolve();
    im.onload = () => {
      if (typeof im.decode === 'function') {
        im.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };
    im.onerror = finish;
    im.src = url;
  });
}

function WelcomePage() {
  const { language } = useLanguage();
  const [featuredPOIs, setFeaturedPOIs] = useState([]);
  const [isLoadingPOIs, setIsLoadingPOIs] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselSlideDir, setCarouselSlideDir] = useState(null);
  const [heroBgA, setHeroBgA] = useState(DEFAULT_WELCOME_HERO_BG);
  const [heroBgB, setHeroBgB] = useState(null);
  /** When true, outgoing (A) fades 0.12→0 and incoming (B) fades 0→0.12. */
  const [heroBgBlendActive, setHeroBgBlendActive] = useState(false);
  /** Avoid progressive/partial CSS background paints on slow networks. */
  const [heroBgBasePaintable, setHeroBgBasePaintable] = useState(false);
  const [heroOverlayFadeGen, setHeroOverlayFadeGen] = useState(0);
  const committedHeroUrlRef = useRef(DEFAULT_WELCOME_HERO_BG);
  const heroBgEffectRunRef = useRef(0);
  const heroOverlayGenRef = useRef(0);
  const heroPendingCommitUrlRef = useRef(null);

  const heroTargetUrl = useMemo(
    () => featuredPOIs[carouselIndex]?.image || DEFAULT_WELCOME_HERO_BG,
    [carouselIndex, featuredPOIs],
  );
  const [modal, setModal] = useState(null);
  const [modalUi, setModalUi] = useState({ open: false, exiting: false });
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  const toggleLanguageMenu = useCallback(() => {
    setLanguageMenuOpen((open) => !open);
  }, []);

  const closeModal = useCallback(() => {
    setModalUi({ open: false, exiting: true });
    window.setTimeout(() => {
      setModal(null);
      setModalUi({ open: false, exiting: false });
    }, 300);
  }, []);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && modal) {
        closeModal();
      }
    };

    if (modal) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [modal, closeModal]);

  useEffect(() => {
    if (!modal) {
      setModalUi({ open: false, exiting: false });
      return;
    }
    setModalUi({ open: false, exiting: false });
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setModalUi({ open: true, exiting: false }));
    });
    return () => cancelAnimationFrame(id);
  }, [modal]);

  const handleHeroBgOverlayTransitionEnd = useCallback((e) => {
    if (e.propertyName !== 'opacity') return;
    if (e.target !== e.currentTarget) return;
    const genAttr = e.currentTarget.getAttribute('data-fade-gen');
    const gen = genAttr != null ? Number(genAttr) : 0;
    if (!gen || gen !== heroOverlayGenRef.current) return;
    const url = heroPendingCommitUrlRef.current;
    if (!url) return;

    heroPendingCommitUrlRef.current = null;
    committedHeroUrlRef.current = url;
    setHeroBgA(url);
    setHeroBgB(null);
    setHeroBgBlendActive(false);
  }, []);

  useEffect(() => {
    if (heroTargetUrl === committedHeroUrlRef.current) return;

    const runId = ++heroBgEffectRunRef.current;
    const rafIds = { outer: 0, inner: 0 };

    const startOverlayFade = (url) => {
      if (runId !== heroBgEffectRunRef.current) return;
      heroOverlayGenRef.current += 1;
      const gen = heroOverlayGenRef.current;
      heroPendingCommitUrlRef.current = url;

      setHeroOverlayFadeGen(gen);
      setHeroBgB(url);
      setHeroBgBlendActive(false);

      rafIds.outer = requestAnimationFrame(() => {
        rafIds.inner = requestAnimationFrame(() => {
          if (runId !== heroBgEffectRunRef.current) return;
          setHeroBgBlendActive(true);
        });
      });
    };

    loadHeroBackgroundImage(heroTargetUrl).then(() => {
      if (runId !== heroBgEffectRunRef.current) return;
      startOverlayFade(heroTargetUrl);
    });

    return () => {
      cancelAnimationFrame(rafIds.outer);
      if (rafIds.inner) cancelAnimationFrame(rafIds.inner);
    };
  }, [heroTargetUrl]);

  useEffect(() => {
    let cancelled = false;
    setHeroBgBasePaintable(false);
    loadHeroBackgroundImage(heroBgA).then(() => {
      if (!cancelled) setHeroBgBasePaintable(true);
    });
    return () => {
      cancelled = true;
    };
  }, [heroBgA]);

  useEffect(() => {
    const fetchFeaturedPOIs = async () => {
      setIsLoadingPOIs(true);
      try {
        const pois = fetchTrendingPOIs();

        const processedPOIs = pois.map((poi) => {
          let firstImage = null;
          try {
            if (poi.image_url) {
              if (typeof poi.image_url === 'string' && poi.image_url.startsWith('[')) {
                const imageArray = JSON.parse(poi.image_url);
                if (Array.isArray(imageArray) && imageArray.length > 0) {
                  firstImage = imageArray[0];
                }
              } else if (typeof poi.image_url === 'string') {
                firstImage = poi.image_url;
              }
            }
          } catch (e) {
            /* skip */
          }
          return {
            id: poi.id,
            name: poi.name,
            name_es: poi.name_es || poi.name,
            image: firstImage,
          };
        });
        const poisWithImages = processedPOIs.filter((poi) => poi.image);
        setFeaturedPOIs(poisWithImages);

        if (poisWithImages.length > 0) {
          await waitForImagesToLoad(poisWithImages);
        }
      } catch (error) {
        console.error('Error fetching trending POIs from cache:', error);
        setFeaturedPOIs([]);
      } finally {
        setCarouselIndex(0);
        setIsLoadingPOIs(false);
      }
    };

    fetchFeaturedPOIs();
  }, [language]);

  const waitForImagesToLoad = (pois) =>
    new Promise((resolve) => {
      const imagesToLoad = pois.filter((poi) => poi.image).map((poi) => poi.image);
      if (imagesToLoad.length === 0) {
        resolve();
        return;
      }
      let loadedCount = 0;
      const totalImages = imagesToLoad.length;
      const bump = () => {
        loadedCount += 1;
        if (loadedCount === totalImages) resolve();
      };
      imagesToLoad.forEach((imageUrl) => {
        const img = new Image();
        img.onload = bump;
        img.onerror = bump;
        img.src = imageUrl;
      });
    });

  const openImageModal = (poi) => {
    if (!poi.image) return;
    setModal({ image: poi.image, title: poiName(poi) });
  };

  const poiName = (poi) => (language === 'es' && poi?.name_es ? poi.name_es : poi?.name) || '';

  const nPois = featuredPOIs.length;
  const prevPoiIndex = nPois > 0 ? (carouselIndex - 1 + nPois) % nPois : 0;
  const nextPoiIndex = nPois > 0 ? (carouselIndex + 1) % nPois : 0;

  const [autoPlayKey, setAutoPlayKey] = useState(0);

  const goCarouselPrev = () => {
    if (nPois < 2) return;
    setCarouselSlideDir('prev');
    setCarouselIndex((i) => (i - 1 + nPois) % nPois);
    setAutoPlayKey((k) => k + 1);
  };

  const goCarouselNext = () => {
    if (nPois < 2) return;
    setCarouselSlideDir('next');
    setCarouselIndex((i) => (i + 1) % nPois);
    setAutoPlayKey((k) => k + 1);
  };

  useEffect(() => {
    if (nPois < 2) return;
    const id = setInterval(() => {
      setCarouselSlideDir('next');
      setCarouselIndex((i) => (i + 1) % nPois);
    }, 5000);
    return () => clearInterval(id);
  }, [nPois, autoPlayKey]);

  const heroTitle =
    language === 'en'
      ? 'The All-in-One App to Explore the Dominican Republic'
      : 'La App Todo-en-Uno para Explorar la Republica Dominicana';

  const heroSubtitle =
    language === 'en'
      ? '100% Free — Discover hidden gems, create personalized itineraries, check sargasso forecasts and explore like a local.'
      : '100% Gratis — Descubre joyas escondidas, crea itinerarios personalizados, consulta pronosticos de sargazo y explora como un local.';

  const touchStartX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      if (delta < 0) goCarouselNext();
      else goCarouselPrev();
    }
    touchStartX.current = null;
  };

  return (
    <div className="welcome-page relative flex min-h-screen flex-col overflow-x-hidden bg-white font-sans text-slate-800">
      {/* Hero: flex-1 fills space above footer on tall viewports */}
      <section
        className="relative flex flex-col overflow-hidden text-white h-screen"
        style={{
          height: '100svh',
          background:
            'linear-gradient(145deg, #041f24 0%, #0c4a5c 38%, #14464F 62%, #1f6d68 100%)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#041f24] via-qq-ocean via-qq-teal to-[#1f6d68]" />
        <div
          className={`pointer-events-none absolute inset-0 bg-cover bg-center [transform:translateZ(0)] ${
            heroBgB ? (heroBgBlendActive ? 'opacity-0' : 'opacity-[0.12]') : 'opacity-[0.12]'
          }`}
          style={{
            backgroundImage: heroBgBasePaintable ? `url('${heroBgA}')` : 'none',
            transition: heroBgB ? `opacity ${HERO_BG_CROSSFADE_MS}ms ease-in-out` : undefined,
          }}
        />
        {heroBgB && (
          <div
            data-fade-gen={heroOverlayFadeGen}
            onTransitionEnd={handleHeroBgOverlayTransitionEnd}
            className={`pointer-events-none absolute inset-0 bg-cover bg-center [transform:translateZ(0)] ${
              heroBgBlendActive ? 'opacity-[0.12]' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${heroBgB}')`,
              transition: `opacity ${HERO_BG_CROSSFADE_MS}ms ease-in-out`,
            }}
            aria-hidden
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/40" />

        <div className="relative z-10 mx-auto flex w-full max-w-6xl min-h-0 flex-1 flex-col justify-center px-4 py-4 sm:px-6 sm:py-6 lg:max-w-7xl lg:px-8 lg:py-8 2xl:max-w-[90rem]">
          <div className="flex flex-1 flex-col w-full rounded-3xl border border-white/20 bg-white/10 shadow-glass backdrop-blur-md">
            <div className="flex flex-1 flex-col w-full px-3 py-3 sm:px-[1.125rem] sm:py-[1.125rem] lg:px-[1.375rem] lg:py-[1.375rem]">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/15">
              <Link
                to="/welcome"
                className="welcome-brand-pill flex items-center gap-3 rounded-full border border-white/60 py-2 pl-2 pr-4 shadow-sm transition hover:border-white/75 sm:pl-3 sm:pr-5"
              >
                <QQIcon className="h-9 w-9 shrink-0 text-qq-teal sm:h-10 sm:w-10" />
                <span
                  className="text-lg font-bold tracking-wide text-qq-teal sm:text-xl"
                  style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                >
                  Quisqueya Quest
                </span>
              </Link>
              <div className="flex items-center bg-transparent p-0">
                <LanguageSelector
                  onPopupToggle={toggleLanguageMenu}
                  isOpen={languageMenuOpen}
                  highResFlags
                  flushChrome
                />
              </div>
            </div>

            <div className="flex flex-1 flex-col justify-center">
              <h1 className="text-center font-serif text-3xl font-medium leading-[1.1] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)] sm:text-4xl md:text-5xl lg:text-[3rem] xl:text-[3.5rem]">
                {language === 'en' ? (
                  <>The All-in-One App to Explore<br />the Dominican Republic</>
                ) : (
                  <>La App Todo-en-Uno para Explorar<br />la Republica Dominicana</>
                )}
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-center text-base font-normal leading-relaxed text-white/90 sm:text-lg">
                {heroSubtitle}
              </p>

              <div className="mx-auto w-full max-w-2xl">
                {/* Carousel — oval POI markers with names */}
                <div
                  className="relative flex items-center justify-center overflow-hidden px-4 sm:px-16 mb-8"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  {isLoadingPOIs ? (
                    <div className="flex items-center justify-center gap-2 py-4 sm:gap-6">
                      {[0, 1, 2].map((k) => (
                        <div
                          key={k}
                          className={`animate-pulse rounded-full bg-white/20 ${
                            k === 1
                              ? 'h-[5.5rem] w-[8rem] sm:h-[9rem] sm:w-[13rem]'
                              : 'h-[2.75rem] w-[4rem] sm:h-[5.5rem] sm:w-[8rem]'
                          }`}
                        />
                      ))}
                    </div>
                  ) : featuredPOIs.length === 0 ? (
                    <div className="min-h-[160px] w-full" aria-hidden />
                  ) : (
                    <>
                      {nPois > 1 && (
                        <button
                          type="button"
                          onClick={goCarouselPrev}
                          className="absolute left-0 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/95 p-0 text-qq-teal shadow-md transition hover:bg-white sm:flex sm:h-11 sm:w-11"
                          aria-label="Previous image"
                        >
                          <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
                        </button>
                      )}
                      {nPois > 1 && (
                        <button
                          type="button"
                          onClick={goCarouselNext}
                          className="absolute right-0 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/95 p-0 text-qq-teal shadow-md transition hover:bg-white sm:flex sm:h-11 sm:w-11"
                          aria-label="Next image"
                        >
                          <FontAwesomeIcon icon={faArrowRight} className="text-lg" />
                        </button>
                      )}
                      <div
                        key={carouselIndex}
                        role="group"
                        aria-roledescription="carousel"
                        onAnimationEnd={(e) => {
                          if (e.target === e.currentTarget) setCarouselSlideDir(null);
                        }}
                        className={`flex items-center justify-center gap-2 sm:gap-5 ${
                          carouselSlideDir === 'next'
                            ? 'animate-carousel-slide-next'
                            : carouselSlideDir === 'prev'
                              ? 'animate-carousel-slide-prev'
                              : ''
                        }`}
                      >
                        {nPois > 1 && (
                          <div className="flex flex-col items-center gap-2">
                            <button
                              type="button"
                              onClick={goCarouselPrev}
                              aria-label={`Previous: ${poiName(featuredPOIs[prevPoiIndex])}`}
                              className="block h-[2.75rem] w-[4rem] shrink-0 overflow-hidden rounded-full border-0 p-0 opacity-75 shadow-lg ring-2 ring-white/20 transition hover:opacity-90 sm:h-[5.5rem] sm:w-[8rem]"
                              style={{ filter: 'blur(0.5px)' }}
                            >
                              <img src={featuredPOIs[prevPoiIndex].image} alt="" className="h-full w-full scale-105 object-cover" />
                            </button>
                            <span className="mt-1 block w-[4rem] truncate text-center text-[10px] font-medium text-white/50 sm:w-[8rem] sm:text-xs">
                              {poiName(featuredPOIs[prevPoiIndex])}
                            </span>
                          </div>
                        )}
                        <div className="flex flex-col items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openImageModal(featuredPOIs[carouselIndex])}
                            className="block h-[5.5rem] w-[8rem] shrink-0 overflow-hidden rounded-full border-4 border-white/70 bg-black/20 p-0 shadow-[0_8px_40px_rgba(0,0,0,0.5)] ring-2 ring-white/40 transition hover:scale-[1.02] sm:h-[9rem] sm:w-[13rem]"
                            aria-label={poiName(featuredPOIs[carouselIndex])}
                          >
                            <img src={featuredPOIs[carouselIndex].image} alt={poiName(featuredPOIs[carouselIndex])} className="h-full w-full object-cover transition duration-500 ease-out" />
                          </button>
                          <span className="mt-1 block whitespace-nowrap text-center text-sm font-bold leading-tight text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)] sm:text-base">
                            {poiName(featuredPOIs[carouselIndex])}
                          </span>
                        </div>
                        {nPois > 1 && (
                          <div className="flex flex-col items-center gap-2">
                            <button
                              type="button"
                              onClick={goCarouselNext}
                              aria-label={`Next: ${poiName(featuredPOIs[nextPoiIndex])}`}
                              className="block h-[2.75rem] w-[4rem] shrink-0 overflow-hidden rounded-full border-0 p-0 opacity-75 shadow-lg ring-2 ring-white/20 transition hover:opacity-90 sm:h-[5.5rem] sm:w-[8rem]"
                              style={{ filter: 'blur(0.5px)' }}
                            >
                              <img src={featuredPOIs[nextPoiIndex].image} alt="" className="h-full w-full scale-105 object-cover" />
                            </button>
                            <span className="mt-1 block w-[4rem] truncate text-center text-[10px] font-medium text-white/50 sm:w-[8rem] sm:text-xs">
                              {poiName(featuredPOIs[nextPoiIndex])}
                            </span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Store buttons — stacked on mobile, side-by-side on sm+ */}
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-5">
                  <a
                    href="https://apple.co/44ajr0e"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full max-w-[16rem] items-center justify-center gap-3 rounded-2xl bg-black px-8 py-4 text-white shadow-xl transition hover:-translate-y-0.5 hover:bg-zinc-900 sm:w-auto sm:px-10 sm:py-5"
                  >
                    <FontAwesomeIcon icon={faApple} className="shrink-0 text-2xl sm:text-3xl" />
                    <div className="text-left leading-tight">
                      <span className="block text-[10px] font-medium uppercase tracking-wide text-white/70">
                        Download on the
                      </span>
                      <span className="block text-base font-semibold sm:text-lg">App Store</span>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.quisqueyaquest.mobile&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full max-w-[16rem] items-center justify-center gap-3 rounded-2xl bg-white px-8 py-4 text-qq-teal shadow-xl transition hover:-translate-y-0.5 hover:bg-zinc-50 sm:w-auto sm:px-10 sm:py-5"
                  >
                    <FontAwesomeIcon icon={faGooglePlay} className="shrink-0 text-2xl sm:text-3xl" />
                    <div className="text-left leading-tight">
                      <span className="block text-[10px] font-medium uppercase tracking-wide text-qq-teal/70">
                        Get it on
                      </span>
                      <span className="block text-base font-semibold sm:text-lg">Google Play</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

      </section>

      <div className="shrink-0">
        <Footer />
      </div>

      {modal && (
        <div
          className={`fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-black/90 p-4 ${
            modalUi.exiting
              ? 'animate-modal-backdrop-out'
              : modalUi.open
                ? 'animate-modal-backdrop-in'
                : 'opacity-0'
          }`}
          role="presentation"
          onClick={closeModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={modal.title}
            className={`relative flex max-h-[90vh] max-w-[90vw] flex-col items-center ${
              modalUi.exiting
                ? 'animate-modal-content-out'
                : modalUi.open
                  ? 'animate-modal-content-in'
                  : 'opacity-0'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative inline-block max-w-full">
              <button
                type="button"
                onClick={closeModal}
                className="absolute right-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full border-0 bg-black/55 p-0 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/70 sm:right-3 sm:top-3"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg" />
              </button>
              <img
                src={modal.image}
                alt={modal.title}
                className="max-h-[80vh] max-w-full rounded-xl object-contain shadow-2xl"
              />
            </div>
            <h3 className="mt-4 rounded-lg bg-black/70 px-6 py-3 text-center text-lg font-semibold text-white backdrop-blur">
              {modal.title}
            </h3>
          </div>
        </div>
      )}
    </div>
  );
}

export default WelcomePage;
